import { ApiFactoryClassType } from './factory';
import { ChatGroupListResponse, ChatGroupResponse } from './types/chat';
import { FormValues } from '../components/form/types';

const chat = (factory: ApiFactoryClassType) => ({
    getChatRoomList: async (
        limit: number = 15,
        offset: number = 0,
        sort?: string,
        order?: 'asc' | 'desc',
    ): Promise<ChatGroupListResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/chat/group/list?limit=${limit}&offset=${offset}${sort && order ? `&sort=${sort}&order=${order}` : ``}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            if (response.status >= 400) {
                return null;
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getChatRoomById: async (
        type: string,
        id: string,
    ): Promise<ChatGroupResponse | null> => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/chat/${type}/${id}`, factory.getApiHost()),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            if (response.status >= 400) {
                return null;
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    updateChatRoom: async (
        type: string,
        id: string,
        chatRoomObj: FormValues,
    ) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/chat/${type}/${id}`, factory.getApiHost()),
                {
                    method: 'PUT',
                    body: JSON.stringify(chatRoomObj),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    createChatRoom: async (type: string, chatRoomObj: FormValues) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/chat/${type}/create`, factory.getApiHost()),
                {
                    method: 'POST',
                    body: JSON.stringify(chatRoomObj),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    deleteChatRoom: async (type: string, id: string) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/chat/${type}/${id}`, factory.getApiHost()),
                {
                    method: 'DELETE',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getChatMessages: async (
        type: string,
        id: string,
        limit: number = 15,
        offset: number = 0,
    ) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/chat/${type}/${id}/messages?limit=${limit}&offset=${offset}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }
            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getReportedMessages: async (
        type = 'pending',
        limit: number = 15,
        offset: number = 0,
    ) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/chat/report/message/list?type=${type}&limit=${limit}&offset=${offset}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }
            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    getReportedMessageById: async (reportId: string) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(
                    `/api/chat/report/message/${reportId}`,
                    factory.getApiHost(),
                ),
                {
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }
            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    lockReportedMessage: async (reportId: string, reason?: string) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/chat/report/message/${reportId}/lock`, factory.getApiHost()),
                {
                    method: 'POST',
                    body: JSON.stringify({reason}),
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
    ignoreReportedMessage: async (reportId: string) => {
        const session = factory.getSession();
        if (!session || !session.auth || !session.auth.access_token) {
            return Promise.reject('no active session');
        }
        try {
            const response = await fetch(
                new URL(`/api/chat/report/message/${reportId}/ignore`, factory.getApiHost()),
                {
                    method: 'POST',
                    headers: {
                        Authorization: 'Bearer ' + session.auth.access_token,
                        'Content-Type': 'application/json',
                    },
                    mode: 'cors',
                },
            );

            if (!response.ok) {
                factory.onError(response);
            }

            return response.json();
        } catch (e) {
            factory.onNetworkError();
            return null;
        }
    },
});

export default chat;
